import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CreditCard, CheckCircle, XCircle, Loader, Euro, Calendar, User } from 'lucide-react';
import paymentService from '../services/paymentService';
import bookingService from '../services/bookingService';

const styles = `
.payment-container {
  max-width: 800px;
  margin: 2rem auto;
  padding: 2rem;
}

.payment-card {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  margin-bottom: 1.5rem;
}

.payment-header {
  text-align: center;
  margin-bottom: 2rem;
}

.payment-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 1rem;
  color: #5A9C9C;
}

.payment-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.payment-subtitle {
  font-size: 1rem;
  color: #6b7280;
}

.booking-summary {
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.summary-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e5e7eb;
}

.summary-item:last-child {
  border-bottom: none;
}

.summary-label {
  font-size: 0.875rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.summary-value {
  font-size: 1rem;
  font-weight: 500;
  color: #374151;
}

.price-breakdown {
  background-color: #f0f9f9;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.price-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
}

.price-label {
  font-size: 0.875rem;
  color: #374151;
}

.price-value {
  font-size: 1rem;
  font-weight: 500;
  color: #374151;
}

.price-total {
  border-top: 2px solid #5A9C9C;
  padding-top: 1rem;
  margin-top: 1rem;
}

.price-total .price-label {
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
}

.price-total .price-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #5A9C9C;
}

.deposit-info {
  background-color: #fff7ed;
  border-left: 4px solid #f59e0b;
  padding: 1rem;
  margin-bottom: 2rem;
  border-radius: 0.25rem;
}

.deposit-info p {
  margin: 0;
  font-size: 0.875rem;
  color: #92400e;
}

.payment-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.btn {
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 0.375rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background: linear-gradient(135deg, #5A9C9C 0%, #4A8080 100%);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(90, 156, 156, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: white;
  color: #374151;
  border: 2px solid #e5e7eb;
}

.btn-secondary:hover {
  background: #f9fafb;
}

.success-container, .error-container {
  text-align: center;
}

.success-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  color: #10b981;
}

.error-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  color: #ef4444;
}

.success-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #10b981;
  margin-bottom: 1rem;
}

.error-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #ef4444;
  margin-bottom: 1rem;
}

.loading-container {
  text-align: center;
  padding: 3rem 0;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  margin: 0 auto 1rem;
  color: #5A9C9C;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 1rem;
  color: #6b7280;
}
`;

const PaymentCheckout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [booking, setBooking] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState(false);

  const bookingId = searchParams.get('bookingId');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      verifyPayment();
    } else if (bookingId) {
      loadBooking();
    } else {
      navigate('/meus-agendamentos');
    }
  }, [bookingId, sessionId]);

  const loadBooking = async () => {
    try {
      setLoading(true);
      const bookings = await bookingService.getBookings();
      const foundBooking = bookings.find(b => b.id === parseInt(bookingId));
      
      if (!foundBooking) {
        navigate('/meus-agendamentos');
        return;
      }

      setBooking(foundBooking);
    } catch (error) {
      console.error('Erro ao carregar booking:', error);
      navigate('/meus-agendamentos');
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async () => {
    try {
      setVerifying(true);
      const payment = await paymentService.verifyPayment(sessionId);
      
      if (payment.status === 'paid') {
        setPaymentSuccess(true);
      } else {
        setPaymentError(true);
      }
    } catch (error) {
      console.error('Erro ao verificar pagamento:', error);
      setPaymentError(true);
    } finally {
      setVerifying(false);
    }
  };

  const handlePayment = async () => {
    try {
      setLoading(true);
      
      const totalAmount = parseFloat(booking.preco.replace('€', '').replace(',', '.'));
      const currentUrl = window.location.origin;
      const successUrl = `${currentUrl}/pagamento?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${currentUrl}/pagamento?bookingId=${bookingId}`;

      const response = await paymentService.createCheckoutSession(
        booking.id,
        totalAmount,
        successUrl,
        cancelUrl
      );

      window.location.href = response.sessionUrl;
    } catch (error) {
      console.error('Erro ao processar pagamento:', error);
      alert('Erro ao processar pagamento. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const calculateDepositAmount = () => {
    if (!booking) return 0;
    const total = parseFloat(booking.preco.replace('€', '').replace(',', '.'));
    return (total / 2).toFixed(2);
  };

  if (verifying) {
    return (
      <>
        <style>{styles}</style>
        <div className="payment-container">
          <div className="payment-card">
            <div className="loading-container">
              <Loader className="loading-spinner" />
              <p className="loading-text">A verificar pagamento...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (paymentSuccess) {
    return (
      <>
        <style>{styles}</style>
        <div className="payment-container">
          <div className="payment-card">
            <div className="success-container">
              <CheckCircle className="success-icon" />
              <h1 className="success-title">Pagamento Confirmado!</h1>
              <p className="payment-subtitle">
                A sua caução foi processada com sucesso. O seu agendamento está confirmado.
              </p>
              <div className="payment-actions" style={{ marginTop: '2rem' }}>
                <button className="btn btn-primary" onClick={() => navigate('/meus-agendamentos')}>
                  Ver Agendamentos
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (paymentError) {
    return (
      <>
        <style>{styles}</style>
        <div className="payment-container">
          <div className="payment-card">
            <div className="error-container">
              <XCircle className="error-icon" />
              <h1 className="error-title">Pagamento Não Processado</h1>
              <p className="payment-subtitle">
                Ocorreu um erro ao processar o seu pagamento. Por favor, tente novamente.
              </p>
              <div className="payment-actions" style={{ marginTop: '2rem' }}>
                <button className="btn btn-secondary" onClick={() => navigate('/meus-agendamentos')}>
                  Voltar aos Agendamentos
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={() => {
                    setPaymentError(false);
                    loadBooking();
                  }}
                >
                  Tentar Novamente
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (loading || !booking) {
    return (
      <>
        <style>{styles}</style>
        <div className="payment-container">
          <div className="payment-card">
            <div className="loading-container">
              <Loader className="loading-spinner" />
              <p className="loading-text">A carregar...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="payment-container">
        <div className="payment-card">
          <div className="payment-header">
            <CreditCard className="payment-icon" />
            <h1 className="payment-title">Pagamento de Caução</h1>
            <p className="payment-subtitle">
              Complete o pagamento para confirmar a sua reserva
            </p>
          </div>

          <div className="booking-summary">
            <h2 className="summary-title">
              <Calendar size={20} />
              Resumo da Reserva
            </h2>
            <div className="summary-item">
              <span className="summary-label">Serviço:</span>
              <span className="summary-value">{booking.servicoNome}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">
                <Calendar size={16} />
                Data:
              </span>
              <span className="summary-value">
                {new Date(booking.dataHora).toLocaleDateString('pt-PT', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
            {booking.children && booking.children.length > 0 && (
              <div className="summary-item">
                <span className="summary-label">
                  <User size={16} />
                  Crianças:
                </span>
                <span className="summary-value">
                  {booking.children.map(c => c.nome).join(', ')}
                </span>
              </div>
            )}
          </div>

          <div className="deposit-info">
            <p>
              <strong>Atenção:</strong> Será cobrado 50% do valor total como caução. 
              O valor restante deverá ser pago no dia do serviço.
            </p>
          </div>

          <div className="price-breakdown">
            <div className="price-row">
              <span className="price-label">Valor Total:</span>
              <span className="price-value">{booking.preco}</span>
            </div>
            <div className="price-row">
              <span className="price-label">Caução (50%):</span>
              <span className="price-value">€{calculateDepositAmount()}</span>
            </div>
            <div className="price-row">
              <span className="price-label">A pagar no dia:</span>
              <span className="price-value">€{calculateDepositAmount()}</span>
            </div>
            <div className="price-row price-total">
              <span className="price-label">Valor a pagar agora:</span>
              <span className="price-value">
                <Euro size={20} style={{ display: 'inline', verticalAlign: 'middle' }} />
                {calculateDepositAmount()}
              </span>
            </div>
          </div>

          <div className="payment-actions">
            <button 
              className="btn btn-secondary" 
              onClick={() => navigate('/meus-agendamentos')}
              disabled={loading}
            >
              Cancelar
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handlePayment}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader size={20} className="loading-spinner" />
                  A processar...
                </>
              ) : (
                <>
                  <CreditCard size={20} />
                  Pagar com Stripe
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentCheckout;